/**
 * The holding bar: while an inventory item is picked up, a bar under the
 * shop's tabs says what is held, what to do with it, and offers to put it
 * down. It sits outside the tab panels, so it shows whichever tab is open —
 * the item is picked up on the Inventory tab but used on the Dinosaurs one,
 * and before this nothing on that tab said anything was held
 * (docs/mockups/holding.html, A).
 */

import type { ItemDef } from '../data/items';
import { itemCursorUrl, itemIconUrl } from './inventory';

export interface HoldingView {
  /** What is held (or null), and how many dinosaurs it could be used on now. */
  /** `why` says why nothing can take it, shown when `targets` is 0. */
  update(item: ItemDef | null, targets: number, why: string): void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** Builds the bar just after `anchor` (the tab strip). */
export function createHoldingBar(anchor: HTMLElement, onPutDown: () => void): HoldingView {
  const bar = el('div', 'holding');
  bar.hidden = true;
  bar.setAttribute('role', 'status');
  const icon = el('img', 'holding__icon');
  icon.alt = '';
  icon.width = 32;
  icon.height = 32;
  const text = el('span', 'holding__text');
  const title = el('span', 'holding__title');
  const hint = el('span', 'holding__hint');
  text.append(title, hint);
  const drop = el('button', 'holding__drop', 'Put down ✕');
  drop.type = 'button';
  drop.addEventListener('click', onPutDown);
  bar.append(icon, text, drop);
  anchor.after(bar);

  let last = '';
  return {
    update(item, targets, why) {
      const key = item ? `${item.id}|${targets > 0}|${why}` : '';
      if (key === last) return;
      last = key;
      bar.hidden = item === null;
      if (!item) return;
      icon.src = itemIconUrl(item);
      title.textContent = `Holding a ${item.name}`;
      hint.textContent = targets > 0 ? 'Choose a dinosaur, in the list or in the yard' : why;
    },
  };
}

/**
 * How far below and right of the pointer's tip the held item's box sits, in
 * px: just clear of the arrow's point, so the two read as one thing.
 */
const FOLLOW_OFFSET = 8;

/**
 * The held item riding beside the pointer, which stays the ordinary arrow or
 * hand — replacing the cursor outright hid what was being pointed at. Mouse
 * and pen only: a finger has no pointer to follow, and the bar says what is
 * held there.
 */
export function createHeldItemFollower(): { show(item: ItemDef | null): void } {
  // In a box like its inventory slot, so it reads as the item picked up.
  const box = el('div', 'held-item');
  box.hidden = true;
  const img = el('img', 'held-item__icon');
  img.alt = '';
  img.width = 32;
  img.height = 32;
  box.append(img);
  document.body.append(box);
  let item: ItemDef | null = null;
  /** The last mouse or pen position, kept while nothing is held too. */
  let at: { x: number; y: number } | null = null;

  const place = (): void => {
    if (!item || !at) {
      box.hidden = true;
      return;
    }
    box.hidden = false;
    box.style.transform = `translate(${at.x + FOLLOW_OFFSET}px, ${at.y + FOLLOW_OFFSET}px)`;
  };

  window.addEventListener(
    'pointermove',
    (ev) => {
      // A finger has no pointer to follow: forget the last one.
      at = ev.pointerType === 'touch' ? null : { x: ev.clientX, y: ev.clientY };
      if (item) place();
    },
    { passive: true },
  );
  document.documentElement.addEventListener('pointerleave', () => {
    box.hidden = true;
  });

  return {
    show(next) {
      item = next;
      if (next) img.src = itemCursorUrl(next);
      // Picked up with a click, so the pointer is already where it last was.
      place();
    },
  };
}
